import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/router";
import { CalendarDays, Keyboard, Plug, Settings2, SunMoon, Users, Zap } from "lucide-react";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { cn } from "@/lib/utils";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "../ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
const menuItemClassName = "group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:w-full -mx-1";
const menuButtonClassName = "h-8 rounded-md p-1.5 group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:mx-auto type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 data-[active=true]:bg-[#0000000f] data-[active=true]:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 data-[active=true]:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:data-[active=true]:bg-white/6 dark:data-[active=true]:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:data-[active=true]:[&_svg]:text-white cursor-pointer";

/**
 * Contextual sidebar for the Settings page. Sits to the right of the
 * app-switcher rail and navigates the settings sections via the page's
 * `?tab=` query param.
 */
export default function SettingsSidebar() {
  const $ = _c(21);
  const router = useRouter();
  const {
    activeGithubOrg,
    codesmithEnabled
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = router.query.org;
  const currentOrg = typeof urlOrg === "string" ? urlOrg : activeGithubOrg;
  const settingsBase = currentOrg ? `/${currentOrg}/settings` : "/settings";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      name: "General",
      tab: "general",
      icon: <Settings2 className="size-[18px]" />
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "Appearance",
      tab: "appearance",
      icon: <SunMoon className="size-[18px]" />
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "Features",
      tab: "features",
      icon: <Zap className="size-[18px]" />
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      name: "Integrations",
      tab: "integrations",
      icon: <Plug className="size-[18px]" />
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      name: "Team",
      tab: "team",
      icon: <Users className="size-[18px]" />
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== codesmithEnabled) {
    t5 = [t0, t1, t2, t3, t4, ...(codesmithEnabled ? [{
      name: "[code]smith",
      tab: "codesmith",
      icon: <CodesmithIcon className="size-[18px]" />
    }] : [])];
    $[5] = codesmithEnabled;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const nav = t5;
  const tabFromUrl = typeof router.query.tab === "string" ? router.query.tab : null;
  let t6;
  if ($[7] !== nav || $[8] !== tabFromUrl) {
    t6 = tabFromUrl && nav.some(item => item.tab === tabFromUrl) ? tabFromUrl : "general";
    $[7] = nav;
    $[8] = tabFromUrl;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const activeTab = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Settings" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== activeTab || $[13] !== nav || $[14] !== settingsBase) {
    t9 = <SidebarContent className="flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden"><SidebarGroup className="border-0 min-h-0 flex-1 overflow-y-auto pt-app-header-gap group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className="flex flex-col gap-1">{nav.map(item_0 => <SidebarMenuItem key={item_0.name} className={menuItemClassName}><SidebarMenuButton asChild={true} isActive={activeTab === item_0.tab} tooltip={item_0.name} className={menuButtonClassName}><Link href={item_0.tab === "general" ? settingsBase : `${settingsBase}?tab=${item_0.tab}`} className="cursor-pointer"><div className="relative">{item_0.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item_0.name}</span></Link></SidebarMenuButton></SidebarMenuItem>)}</SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarContent>;
    $[12] = activeTab;
    $[13] = nav;
    $[14] = settingsBase;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SidebarMenuItem className={menuItemClassName}><SidebarMenuButton asChild={true} tooltip="Book a demo" className={menuButtonClassName}><a href="https://www.blacksmith.sh/contact-our-team" target="_blank" rel="noopener noreferrer"><div className="relative"><CalendarDays className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Book a demo</span></a></SidebarMenuButton></SidebarMenuItem>;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0"><SidebarGroup className="border-0 py-2 group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className="flex flex-col gap-1">{t10}<SidebarMenuItem className={menuItemClassName}><SidebarMenuButton tooltip="Keyboard shortcuts" className={menuButtonClassName} onClick={_temp}><div className="relative"><Keyboard className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Keyboard shortcuts</span></SidebarMenuButton></SidebarMenuItem></SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarFooter>;
    t12 = <SidebarRail />;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t9) {
    t13 = <ShadcnSidebar collapsible="icon" className={t7}>{t8}{t9}{t11}{t12}</ShadcnSidebar>;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
}
function _temp() {
  return document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "?",
    bubbles: true,
    cancelable: true
  }));
}